import { useSlideEditorContext } from "@/components/slides/editor/SlideEditorContext";
import { useSelection } from "@/contexts/SelectionContext";
import { SlideFieldsFragmentDoc } from "@/generated/graphql";
import { FREEFORM_CANVAS_SLIDE } from "@/lib/constants/slides";
import { apolloClient } from "@/lib/graphql-client";
import { useSpellChecker } from "@/lib/hooks/shared/useSpellChecker";
import { useSlideScreenshot } from "@/lib/hooks/slides/useSlideScreenshot";
import { useUpdateSlide } from "@/lib/hooks/slides/useUpdateSlide";
import {
  convertElementsToPercentages,
  convertElementsToPixels,
} from "@/lib/utils/elementConversion";
import { saveScrollTarget } from "@/lib/utils/scrollRestoration";
import { useAppDispatch, useAppSelector, useAppStore } from "@/store/hooks";
import {
  clearSlide,
  loadSlide,
  setSelectedId,
  updateSlideName,
  updateSlide as updateSlideRedux,
} from "@/store/slideEditorSlice";
import type { SlideFields } from "@/types/Slide";
import type { ReadingSlideData, SlideData, SlideType } from "@ai-teach/types";
import { resolveSlideTemplate } from "@ai-teach/types";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { ActionCreators } from "redux-undo";

export function useEditorActions(options?: { enableKeyboardShortcuts?: boolean }) {
  const enableKeyboardShortcuts = options?.enableKeyboardShortcuts ?? false;
  const [isSaving, setIsSaving] = useState(false);
  const [isLeavePageModalOpen, setIsLeavePageModalOpen] = useState(false);

  const lastSavedStateRef = useRef<string>("");

  // Redux state
  const slide = useAppSelector((state) => state.slideEditor.present.slide);
  const canUndo = useAppSelector((state) => state.slideEditor.past.length > 1);
  const canRedo = useAppSelector((state) => state.slideEditor.future.length > 0);

  const dispatch = useAppDispatch();
  const store = useAppStore();
  const navigate = useNavigate();

  const slideId = slide?.id;
  const slideType = slide?.type;
  const lessonId = slide?.lessonId;

  // Custom hooks
  const { captureScreenshotAsFile } = useSlideScreenshot(slideId ?? "");
  const { updateSlide, loading: updateElementsLoading } = useUpdateSlide();
  const { toggleUnlinkModalOpen, toggleWarningModal } = useSelection();
  const { canvasRef } = useSlideEditorContext();
  const { checkAllPassages } = useSpellChecker();

  // Track the last loaded slide ID to detect when a new slide is opened
  const lastLoadedSlideIdRef = useRef<string | null>(null);

  // Initialize lastSavedStateRef when a new slide is loaded
  useEffect(() => {
    if (!slide) {
      // Reset when no slide
      lastSavedStateRef.current = "";
      lastLoadedSlideIdRef.current = null;
      return;
    }

    // Only initialize when a different slide is loaded
    if (lastLoadedSlideIdRef.current !== slide.id) {
      lastSavedStateRef.current = JSON.stringify({
        elements: slide.elements,
        name: slide.name,
        slideData: slide.slideData,
      });
      lastLoadedSlideIdRef.current = slide.id;
    }
  }, [slide]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!slide) return false;

    const cachedSlide = apolloClient.cache.readFragment({
      id: `Slide:${slideId}`,
      fragment: SlideFieldsFragmentDoc,
    });

    const currentState = JSON.stringify({
      elements: slide.elements,
      name: slide.name,
      slideData: slide.slideData,
    });

    if (!cachedSlide) {
      return currentState !== lastSavedStateRef.current;
    }

    const elementsInPixels = convertElementsToPixels(
      cachedSlide.elements,
      resolveSlideTemplate(cachedSlide)
    );

    const elementsChanged = JSON.stringify(elementsInPixels) !== JSON.stringify(slide.elements);
    const slideDataChanged =
      JSON.stringify(cachedSlide.slideData) !== JSON.stringify(slide.slideData);
    const limitationChanged =
      JSON.stringify(cachedSlide.limitation) !== JSON.stringify(slide.limitation);

    if (
      elementsChanged ||
      slideDataChanged ||
      limitationChanged ||
      cachedSlide.name !== slide.name
    ) {
      return true;
    }

    return false;
  }, [slide, slideId]);

  // Handlers
  const handleSave = useCallback(
    async (shouldGenerateScreenshot = false) => {
      setIsSaving(true);
      if (!lessonId || !slideId || !slide) return;

      if (slide.isLinkedCopy) {
        toggleUnlinkModalOpen({ type: "Slide", itemId: slideId });
        setIsSaving(false);
        return;
      }

      const toastId = toast.loading("Saving slide...");

      // A save runs in the background: the user can leave this slide and open or create
      // another one before it resolves. The mutation itself is addressed by slideId and
      // stays correct, but applying its result to the editor would replace whichever
      // slide is open by then — so every write back into Redux is gated on the editor
      // still holding this slide. The live store is the only reliable source here: this
      // hook's component unmounts on navigation, freezing any local ref or closure.
      const isStillEditingThisSlide = () =>
        store.getState().slideEditor.present.slide?.id === slideId;

      try {
        // Check spelling for reading slides before saving
        let slideDataToSave = slide.slideData as SlideData;

        if (slideType === "reading") {
          const readingSlideData = slide.slideData as ReadingSlideData;
          const needsSpellCheck = readingSlideData.passages.some(
            (p) => p.spellCheckStatus === "unchecked"
          );
          if (needsSpellCheck) {
            const { updatedPassages } = await checkAllPassages(readingSlideData.passages);
            slideDataToSave = {
              ...readingSlideData,
              passages: updatedPassages,
            } as ReadingSlideData;

            // Update Redux state immediately so UI reflects the spell check status
            dispatch(
              updateSlideRedux({
                slideId,
                slideData: slideDataToSave,
              })
            );
          } else {
            slideDataToSave = readingSlideData;
          }
        }

        const elementsInPercentages = convertElementsToPercentages(
          slide.elements,
          resolveSlideTemplate(slide)
        );

        let screenshotFile: File | null = null;

        const screenshotRule = slideType ? FREEFORM_CANVAS_SLIDE[slideType] : [];

        const needScreenshot = screenshotRule.includes(slide.knowledgeArea);

        if (shouldGenerateScreenshot && needScreenshot) {
          try {
            screenshotFile = (await captureScreenshotAsFile(canvasRef, slideId)) ?? null;
          } catch (error) {
            console.debug("Screenshot capture failed", error);
            screenshotFile = null;
          }
        }

        try {
          const updatedSlide = await updateSlide(
            {
              slideId,
              elements: elementsInPercentages,
              name: slide.name,
              slideData: slideDataToSave,
              limitation: slide.limitation,
            },
            screenshotFile
          );

          if (updatedSlide && isStillEditingThisSlide()) {
            dispatch(
              loadSlide({
                ...updatedSlide,
                elements: convertElementsToPixels(
                  updatedSlide.elements,
                  resolveSlideTemplate(updatedSlide)
                ),
              } as SlideFields)
            );
          }

          toast.success("Slide saved successfully!", { id: toastId });
        } catch (error) {
          const message = (error as Error).message || "Failed to save item. Please try again.";
          if (message === "This is a linked copy , unlink it first") {
            toggleUnlinkModalOpen({ type: "Slide", itemId: slideId });
          }
          toast.error(message, { id: toastId });
          throw error;
        }

        if (isStillEditingThisSlide()) {
          lastSavedStateRef.current = JSON.stringify({
            elements: slide.elements,
            name: slide.name,
            slideData: slide.slideData,
          });
        }
      } catch (error) {
        toast.error("Failed to save!", { id: toastId });
        console.error(error);
      } finally {
        setIsSaving(false);
      }
    },
    [
      lessonId,
      slideId,
      slideType,
      slide,
      canvasRef,
      updateSlide,
      slide?.isLinkedCopy,
      captureScreenshotAsFile,
      toggleUnlinkModalOpen,
      checkAllPassages,
      dispatch,
      store,
    ]
  );

  // Selects whichever element the undo/redo step actually changed.
  const selectElementChangedBy = useCallback(
    (action: ReturnType<typeof ActionCreators.undo | typeof ActionCreators.redo>) => {
      const before = store.getState().slideEditor.present.slide?.elements ?? [];
      dispatch(action);
      const after = store.getState().slideEditor.present.slide?.elements ?? [];

      const beforeById = new Map(before.map((el) => [el.id, el]));
      const changed = after.find(
        (el) => JSON.stringify(el) !== JSON.stringify(beforeById.get(el.id))
      );

      if (changed) dispatch(setSelectedId(changed.id));
    },
    [dispatch, store]
  );

  const handleUndo = useCallback(() => {
    selectElementChangedBy(ActionCreators.undo());
  }, [selectElementChangedBy]);

  const handleRedo = useCallback(() => {
    selectElementChangedBy(ActionCreators.redo());
  }, [selectElementChangedBy]);

  const handleClear = useCallback(() => {
    dispatch(clearSlide());
  }, [dispatch]);

  const performLeave = useCallback(
    async (shouldSave: boolean) => {
      if (shouldSave) {
        await handleSave(true);
      }
      // Reset tracking refs
      lastSavedStateRef.current = "";
      lastLoadedSlideIdRef.current = null;
      dispatch(clearSlide());
      if (!lessonId) return;
      saveScrollTarget(`slide-${slideId}`, lessonId);
      navigate({ to: "/admin/lesson/$lessonId", params: { lessonId } });
    },
    [handleSave, dispatch, lessonId, navigate, slideId]
  );

  const handleLeavePage = useCallback(
    (shouldSave: boolean) => {
      if (shouldSave || !hasUnsavedChanges) {
        performLeave(shouldSave);
        return;
      }

      toggleWarningModal("You have unsaved changes. Do you want to save before leaving?", {
        onConfirm: () => {
          // Save and leave
          performLeave(true);
        },
        onCancel: () => {
          // Leave without saving
          performLeave(false);
        },
        confirmLabel: "Save",
        cancelLabel: "Don't Save",
      });
    },

    [hasUnsavedChanges, toggleWarningModal, performLeave]
  );

  const handleNameChange = useCallback(
    (newName: string) => {
      dispatch(updateSlideName(newName));
    },
    [dispatch]
  );

  const openLeavePageModal = useCallback(() => {
    setIsLeavePageModalOpen(true);
  }, []);

  const closeLeavePageModal = useCallback(() => {
    setIsLeavePageModalOpen(false);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    if (!enableKeyboardShortcuts) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key.toLowerCase() === "z" && !e.shiftKey) {
          e.preventDefault();
          if (canUndo) selectElementChangedBy(ActionCreators.undo());
        } else if (e.key.toLowerCase() === "z" && e.shiftKey) {
          e.preventDefault();
          if (canRedo) selectElementChangedBy(ActionCreators.redo());
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enableKeyboardShortcuts, canUndo, canRedo, selectElementChangedBy]);

  // Warn user before leaving the page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        // Most browsers require returnValue to be set
        e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  return {
    // State
    isSaving,
    isLeavePageModalOpen,
    name: slide?.name || "",
    canUndo,
    canRedo,
    updateElementsLoading,
    hasUnsavedChanges,

    // Handlers
    handleSave,
    handleUndo,
    handleRedo,
    handleClear,
    handleLeavePage,
    handleNameChange,
    openLeavePageModal,
    closeLeavePageModal,
  };
}
